import React, { useEffect, useRef } from 'react'
import { Platform } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRoute } from '@react-navigation/native'
import { BottomView } from '@zet/common/components/bottomView'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import ScreenViewEventWrapper from '@zet/common/components/screenViewEventWrapper'
import {
  useAddCustomerNextState,
  useAddCustomerNextStateWeb,
} from '@zet/customer/src/api/hooks'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Input } from '@zet/design-system/input'
import { Stack, YStack } from '@zet/design-system/layout'
import { B1, B2, B3, B5, H5 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import AddCustomerBackHandlerHOC from '../../components/addCustomerBackHandlerHOC'

const validationSchema = z
  .object({
    panNumber: z
      .string()
      .min(1, { message: 'Field is required!' })
      .max(10)
      .regex(
        new RegExp(/(^([a-zA-Z]{5})([0-9]{4})([a-zA-Z]{1})$)/),
        'Invalid PAN'
      ),
  })
  .required()

type ValidationSchema = z.infer<typeof validationSchema>

const PanDetails = () => {
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    watch,
    setError,
    clearErrors,
    setValue,
  } = useForm<ValidationSchema>({
    mode: 'all',
    resolver: zodResolver(validationSchema),
  })
  const { params } = useRoute()

  const { mutate, data, isLoading } = useAddCustomerNextState()
  const { mutate: mutateForWeb, isLoading: isLoadingCrif } =
    useAddCustomerNextStateWeb()
  const screen_name = useRef({})

  // screenName
  useEffect(() => {
    if (data?.actionData?.panNo) {
      setValue('panNumber', data?.actionData?.panNo)
    }
  }, [data, setValue])
  const { isWebFlow } = useAddCustomerStore((store) => store)

  // On web referesh this useeffect will run
  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      !isWebFlow &&
      data?.UI_isAddCustomerNewFlow &&
      params?.customerId
    ) {
      mutateForWeb({
        customerId: params?.customerId,
        screenName: 'GET_CURRENT_STATE',
        fullName: params?.customerName,
        phoneNumber: params?.customerPhoneNumber,
      })
    }
  }, [
    data?.UI_isAddCustomerNewFlow,
    isWebFlow,
    mutateForWeb,
    params?.customerId,
  ])

  useEffect(() => {
    screen_name.current =
      params?.screenName === 'SHOWN_BASIC_DETAILS_SCREEN'
        ? `new_add_customer_pan_input${
            Platform.OS === 'web' ? '_web' : '_app'
          }_step3`
        : `new_add_customer_pan_input${
            Platform.OS === 'web' ? '_web' : '_app'
          }_step4`
  }, [params?.screenName])

  const onsubmit = () => {
    const { panNumber } = watch()
    if (Platform.OS === 'web') {
      const urlParams = new URLSearchParams(window.location.search)
      const hash = urlParams.get('hashNew')
      mutateForWeb(
        {
          panNo: panNumber,
          customerId: params?.customerId,
          screenName: 'SHOWN_PAN_COLLECTION_SCREEN',
          hash: hash as string,
        },
        {
          onError: (err) => {
            if (err.response.data.error.code === 400) {
              setError('panNumber', {
                message: err.response.data.error.message,
              })
            }
          },
          onSuccess: (data) => {
            if (data?.code === 400) {
              setError('panNumber', {
                message: data?.message,
              })
            }
          },
        }
      )
    } else {
      mutate(
        {
          panNo: panNumber,
          customerId: params?.customerId,
          screenName: 'SHOWN_PAN_COLLECTION_SCREEN',
        },
        {
          onError: (err) => {
            if (err.response.data.error.code === 400) {
              setError('panNumber', {
                message: err.response.data.error.message,
              })
            }
          },
          onSuccess: (data) => {
            if (data?.code === 400) {
              setError('panNumber', {
                message: data?.message,
              })
            }
          },
        }
      )
    }
  }
  return (
    <LazyHOC>
      <ScreenViewEventWrapper screenName={screen_name.current?.toString()}>
        <AddCustomerBackHandlerHOC>
          <KeyBoardWrapper
            className="flex-1 bg-white"
            BottomSheet={
              <BottomView
                onPress={handleSubmit(onsubmit)}
                stepCount={
                  params?.screenName === 'SHOWN_BASIC_DETAILS_SCREEN' ? 3 : 4
                }
                maxCount={4}
                disabled={!isValid}
                loading={isLoading || isLoadingCrif}
                buttonEvent={{
                  eventName: 'click_action',
                  payload: {
                    item_name:
                      params?.screenName === 'SHOWN_BASIC_DETAILS_SCREEN'
                        ? `new_add_customer_pan_input${
                            Platform.OS === 'web' ? '_web' : '_app'
                          }_step3`
                        : `new_add_customer_pan_input${
                            Platform.OS === 'web' ? '_web' : '_app'
                          }_step4`,
                  },
                }}
              />
            }
          >
            <Stack className="flex-1 bg-white">
              <H5 className="border-grays-30 text-grays-100 border-b p-3">
                {i18n.t('customerDetails.panScreen.heading')}
              </H5>
              <YStack className="mt-3 px-3">
                <B3 className="text-grays-100 mb-3">
                  {i18n.t('customerDetails.panScreen.subHeading')}
                </B3>
                <Stack className="mt-[28]">
                  <B2 className="text-grays-100 mb-1">
                    {i18n.t('customerDetails.pan')}
                    <B1 className="text-error-100 ml-1">*</B1>
                  </B2>

                  <Controller
                    control={control}
                    render={({
                      field: { onChange, onBlur, value },
                      fieldState: { isTouched },
                    }) => (
                      <Input
                        placeholder="Eg: NVLNK4546"
                        onBlur={onBlur}
                        onChangeText={(value) => {
                          clearErrors('panNumber')
                          onChange(value)
                        }}
                        value={value}
                        type={
                          errors?.panNumber?.message && isTouched
                            ? 'error'
                            : errors?.panNumber?.message ===
                              'PAN already exists'
                            ? 'warning'
                            : 'primary'
                        }
                        autoCapitalize="characters"
                        maxLength={10}
                      />
                    )}
                    name="panNumber"
                  />
                  {errors.panNumber?.message ? (
                    <Stack className="mt-2">
                      <B5 className="text-error-100">
                        {errors.panNumber?.message}
                      </B5>
                    </Stack>
                  ) : null}
                </Stack>
              </YStack>
            </Stack>
          </KeyBoardWrapper>
        </AddCustomerBackHandlerHOC>
      </ScreenViewEventWrapper>
    </LazyHOC>
  )
}

export default PanDetails
